import React from "react";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { useCart } from "@/lib/cart";
import { formatPrice, resolveImage } from "@/lib/format";
import { Link } from "react-router-dom";
import { Minus, Plus, X, ShoppingBag } from "lucide-react";

export default function CartDrawer() {
  const { items, isOpen, close, setQty, remove, subtotal } = useCart();
  return (
    <Sheet open={isOpen} onOpenChange={(v) => !v && close()}>
      <SheetContent className="w-full sm:max-w-md bg-cream border-l border-brand flex flex-col" data-testid="cart-drawer">
        <SheetHeader>
          <SheetTitle className="font-serif-display text-2xl text-plum-deep">Your cart</SheetTitle>
          <SheetDescription className="sr-only">Review items in your cart and proceed to checkout.</SheetDescription>
        </SheetHeader>
        <div className="mt-6 flex-1 overflow-auto">
          {items.length === 0 ? (
            <div className="text-center py-20 text-plum/60">
              <ShoppingBag className="h-8 w-8 mx-auto mb-3 text-gold" />
              <div>Your cart is empty.</div>
              <Link to="/shop" onClick={close} className="inline-block mt-6 text-sm underline text-plum-deep" data-testid="cart-browse-link">Browse the shop</Link>
            </div>
          ) : (
            <ul className="space-y-5">
              {items.map((i) => (
                <li key={i.product_id} className="flex gap-4 pb-5 border-b border-brand" data-testid={`cart-item-${i.product_id}`}>
                  <div className="w-20 aspect-[4/5] product-media">
                    {i.image_url ? <img src={resolveImage(i.image_url)} alt="" /> : <div className="h-full w-full ph-checker" />}
                  </div>
                  <div className="flex-1 flex flex-col justify-between">
                    <div className="flex items-start justify-between">
                      <div className="text-sm font-medium text-plum-deep">{i.title}</div>
                      <button className="text-plum/50 hover:text-plum-deep" onClick={() => remove(i.product_id)} data-testid={`cart-remove-${i.product_id}`}><X className="h-4 w-4" /></button>
                    </div>
                    <div className="flex items-center justify-between mt-2">
                      <div className="inline-flex items-center border border-brand rounded-full bg-white">
                        <button className="h-8 w-8 inline-flex items-center justify-center text-plum" onClick={() => setQty(i.product_id, Math.max(1, i.quantity - 1))} data-testid={`cart-dec-${i.product_id}`}><Minus className="h-3 w-3" /></button>
                        <span className="text-sm min-w-6 text-center text-plum-deep">{i.quantity}</span>
                        <button className="h-8 w-8 inline-flex items-center justify-center text-plum" onClick={() => setQty(i.product_id, Math.min(20, i.quantity + 1))} data-testid={`cart-inc-${i.product_id}`}><Plus className="h-3 w-3" /></button>
                      </div>
                      <div className="text-sm text-plum-deep">{formatPrice(i.price * i.quantity)}</div>
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
        {items.length > 0 && (
          <div className="mt-6 border-t border-brand pt-5 space-y-4">
            <div className="flex items-center justify-between">
              <span className="text-sm text-plum/70">Subtotal</span>
              <span className="font-serif-display text-xl text-plum-deep" data-testid="cart-subtotal">{formatPrice(subtotal)}</span>
            </div>
            <Link
              to="/checkout"
              onClick={close}
              className="btn-primary w-full justify-center"
              data-testid="go-to-checkout-btn"
            >
              Checkout
            </Link>
            <div className="text-[11px] text-plum/60 text-center">Instant download after purchase.</div>
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
}
